import domtoimage from "dom-to-image-more";
import { translate } from "../../../../../translate/translate";

export const NPI_DASHBOARD_CAPTURE_ID = "npi-dashboard-capture";

interface ExportDashboardImageOptions {
  clientName: string;
  year: number | "";
  projectTypeName: string;
}

export const sanitizeFilenamePart = (value: string): string =>
  value.trim().replace(/[^\w-]+/g, "_");

export const createTextElement = (
  text: string,
  styles: Partial<CSSStyleDeclaration>,
): HTMLDivElement => {
  const element = document.createElement("div");
  element.textContent = text;

  Object.assign(element.style, {
    fontFamily: "inherit",
    color: "#1E293B",
    ...styles,
  });

  return element;
};

const preservesTextEllipsis = (element: HTMLElement): boolean => {
  const style = window.getComputedStyle(element);
  return (
    style.textOverflow === "ellipsis" &&
    ["hidden", "clip"].includes(style.overflowX)
  );
};

export const exportNpiDashboardImage = async ({
  clientName,
  year,
  projectTypeName
}: ExportDashboardImageOptions): Promise<void> => {

  const dashboardElement = document.getElementById(NPI_DASHBOARD_CAPTURE_ID);
  if (!dashboardElement) {
    throw new Error("Dashboard element not found");
  }

  const generatedAt = new Date();
  const dashboardSubtitle = [projectTypeName, clientName, year]
    .filter(Boolean)
    .join(" · ");

  const headerElement = createTextElement(
    `NPI Dashboard${dashboardSubtitle ? ` - ${dashboardSubtitle}` : ""}`,
    { fontSize: "18px", fontWeight: "700", marginBottom: "12px" },
  );
  const footerElement = createTextElement(
    `${translate.t("generated_at")} ${generatedAt.toLocaleString(
      translate.language === "pt" ? "pt-BR" : "en-US",
    )}`,
    {
      fontSize: "12px",
      color: "#64748B",
      marginTop: "12px",
      textAlign: "right",
    },
  );

  const clone = dashboardElement.cloneNode(true) as HTMLElement;
  const container = document.createElement("div");
  const originalWidth = dashboardElement.getBoundingClientRect().width;

  Object.assign(container.style, {
    position: "fixed",
    left: "-100000px",
    top: "0",
    width: "max-content",
    height: "max-content",
    overflow: "visible",
    backgroundColor: "#ffffff",
    pointerEvents: "none",
    zIndex: "-1",
    visibility: "visible",
  });

  Object.assign(clone.style, {
    width: `${originalWidth}px`,
    height: "auto",
    maxWidth: "none",
    maxHeight: "none",
    overflow: "visible",
    padding: "16px",
    boxSizing: "border-box",
  });

  clone.prepend(headerElement);
  clone.append(footerElement);
  container.appendChild(clone);
  document.body.appendChild(container);

  try {
    await document.fonts.ready;
    const originais: HTMLElement[] = [
      dashboardElement,
      ...Array.from(
        dashboardElement.querySelectorAll<HTMLElement>("*")
      ),
    ];
    const clonados: HTMLElement[] = [
      clone,
      ...Array.from(clone.querySelectorAll<HTMLElement>("*")).filter(
        (element) => element !== headerElement && element !== footerElement,
      ),
    ];

    originais.forEach((original, index) => {
      const copia = clonados[index];

      if (!copia) return;
      if (copia.closest("#dashboard-legenda")) return;
      if (preservesTextEllipsis(original)) return;

      const estilo = window.getComputedStyle(original);

      if ( original.scrollWidth > original.clientWidth ||
        original.scrollHeight > original.clientHeight ||
        ["auto", "scroll"].includes(estilo.overflowX) ||
        ["auto", "scroll"].includes(estilo.overflowY) ) {
        Object.assign(copia.style, {
          width: `${Math.max(
            original.scrollWidth,
            original.clientWidth
          )}px`,
          height: `${Math.max(
            original.scrollHeight,
            original.clientHeight
          )}px`,
          minWidth: "0",
          minHeight: "0",
          maxWidth: "none",
          maxHeight: "none",
          overflow: "visible",
          boxSizing: "border-box",
        });
      }
    });

    clone.querySelectorAll<HTMLElement>("[data-export-ignore]").forEach((element) => {
      element.remove();
    });

    const elementos = Array.from(
      clone.querySelectorAll<HTMLElement>("*")
    );

    elementos.reverse().forEach((el) => {
      if (el.closest("#dashboard-legenda")) return;
      if (preservesTextEllipsis(el)) return;

      if (el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight) {
        Object.assign(el.style, {
          width: `${Math.max(
            el.scrollWidth,
            el.clientWidth
          )}px`,
          height: `${Math.max(
            el.scrollHeight,
            el.clientHeight
          )}px`,
          maxWidth: "none",
          maxHeight: "none",
          overflow: "visible",
          boxSizing: "border-box",
        });
      }
    });

    const containerBorda = clone.querySelector<HTMLElement>(
      "#dashboard-container-export"
    );

    if (containerBorda) {
      Object.assign(containerBorda.style, {
        width: `${Math.ceil(containerBorda.scrollWidth)}px`,
        height: `${Math.ceil(containerBorda.scrollHeight)}px`,
        maxWidth: "none",
        maxHeight: "none",
        overflow: "visible",
        boxSizing: "border-box",
      });
    }

    void clone.offsetHeight;

    const width = Math.ceil(clone.scrollWidth);
    const height = Math.ceil(clone.scrollHeight);

    Object.assign(clone.style, {
      width: `${width}px`,
      height: `${height}px`,
      maxWidth: "none",
      maxHeight: "none",
      overflow: "visible",
    });

    const imageDataUrl = await domtoimage.toPng(clone, {
      width,
      height,
      bgcolor: "#ffffff",
      style: {
        width: `${width}px`,
        height: `${height}px`,
        maxWidth: "none",
        maxHeight: "none",
        overflow: "visible",
      },
    });

    const filenameParts = [
      sanitizeFilenamePart(projectTypeName),
      sanitizeFilenamePart(clientName),
      year,
    ].filter(Boolean);

    const downloadLink = document.createElement("a");
    downloadLink.href = imageDataUrl;
    downloadLink.download = `NPI-Dashboard_${filenameParts.join("_")}.png`;
    downloadLink.click();

  } finally {
    headerElement.remove();
    footerElement.remove();
    container.remove();
  }
};
